/* Shop by sound (/pages/shop-by-sound, /pages/sound/<handle>) and Shop by genre (/pages/shop-by-genre,
   /pages/genre/<handle>). Curves are illustrative EQ shapes computed from each Sound entry's filters (W3C Audio EQ
   Cookbook biquads, aligned to 60 dB at 1 kHz), never product measurements.
   Every visible string comes from section settings (JSON config / data attributes). */
(() => {
  'use strict';

  const NS = 'http://www.w3.org/2000/svg';
  const FS = 96000;
  const DOMAIN_DB = [30, 85];
  const REF_DB = 60;
  const MAJOR = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  const MINOR = [30, 40, 60, 70, 80, 90, 300, 400, 600, 700, 800, 900, 3000, 4000, 6000, 7000, 8000, 9000];
  const LINE = ['#00FFFF', '#FFB454', '#B59CFF', '#70CFAD', '#F28CB8', '#B5C5EE'];
  const ZOOM = { all: [20, 20000], bass: [20, 250], mids: [250, 5000], highs: [5000, 20000] };
  const BG = '#111214';

  const esc = (x) => String(x ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (tpl, map) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const json = (el) => { try { return JSON.parse(el.textContent); } catch (e) { return null; } };
  const el = (tag, attrs = {}, text) => {
    const n = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => { if (v !== null && v !== undefined) n.setAttribute(k, v); });
    if (text !== undefined) n.textContent = text;
    return n;
  };

  /* ---------- EQ model (RBJ biquads) ---------- */
  function coefficients(f) {
    const A = Math.pow(10, f.gain / 40), w = 2 * Math.PI * f.hz / FS, c = Math.cos(w), s = Math.sin(w);
    let b0, b1, b2, a0, a1, a2;
    if (f.type === 'peak') {
      const alpha = s / (2 * f.Q);
      b0 = 1 + alpha * A; b1 = -2 * c; b2 = 1 - alpha * A; a0 = 1 + alpha / A; a1 = -2 * c; a2 = 1 - alpha / A;
    } else {
      const alpha = s / 2 * Math.sqrt((A + 1 / A) * (1 / f.S - 1) + 2), t = 2 * Math.sqrt(A) * alpha;
      if (f.type === 'lowShelf') {
        b0 = A * ((A + 1) - (A - 1) * c + t); b1 = 2 * A * ((A - 1) - (A + 1) * c); b2 = A * ((A + 1) - (A - 1) * c - t);
        a0 = (A + 1) + (A - 1) * c + t; a1 = -2 * ((A - 1) + (A + 1) * c); a2 = (A + 1) + (A - 1) * c - t;
      } else {
        b0 = A * ((A + 1) + (A - 1) * c + t); b1 = -2 * A * ((A - 1) + (A + 1) * c); b2 = A * ((A + 1) + (A - 1) * c - t);
        a0 = (A + 1) - (A - 1) * c + t; a1 = 2 * ((A - 1) - (A + 1) * c); a2 = (A + 1) - (A - 1) * c - t;
      }
    }
    return { b: [b0 / a0, b1 / a0, b2 / a0], a: [1, a1 / a0, a2 / a0] };
  }
  function gain(filters, hz) {
    const w = 2 * Math.PI * hz / FS, c = Math.cos(w), s = Math.sin(w), c2 = Math.cos(2 * w), s2 = Math.sin(2 * w);
    let sum = 0;
    filters.forEach((f) => {
      const { b, a } = f.coef || (f.coef = coefficients(f));
      const nr = b[0] + b[1] * c + b[2] * c2, ni = -b[1] * s - b[2] * s2, dr = a[0] + a[1] * c + a[2] * c2, di = -a[1] * s - a[2] * s2;
      sum += 10 * Math.log10((nr * nr + ni * ni) / (dr * dr + di * di));
    });
    return sum;
  }
  /* "hz:db, hz:db, …" -> smooth level function (Catmull-Rom in log frequency) */
  function traced(text) {
    const pts = String(text || '').split(/[,\n]+/).map((p) => p.split(':').map(Number))
      .filter(([hz, db]) => hz > 0 && Number.isFinite(db)).sort((a, b) => a[0] - b[0]).map(([hz, db]) => [Math.log10(hz), db]);
    if (pts.length < 2) return null;
    return (hz) => {
      const lx = Math.log10(hz), n = pts.length;
      if (lx <= pts[0][0]) return pts[0][1];
      if (lx >= pts[n - 1][0]) return pts[n - 1][1];
      let i = 0;
      while (i < n - 2 && pts[i + 1][0] < lx) i++;
      const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(n - 1, i + 2)];
      const t = (lx - p1[0]) / (p2[0] - p1[0]), t2 = t * t, t3 = t2 * t;
      const m1 = (p2[1] - p0[1]) / (p2[0] - p0[0]) * (p2[0] - p1[0]), m2 = (p3[1] - p1[1]) / (p3[0] - p1[0]) * (p2[0] - p1[0]);
      return (2 * t3 - 3 * t2 + 1) * p1[1] + (t3 - 2 * t2 + t) * m1 + (-2 * t3 + 3 * t2) * p2[1] + (t3 - t2) * m2;
    };
  }
  function prepare(sounds) {
    sounds.forEach((s) => {
      let f = s.filters;
      if (typeof f === 'string') { try { f = JSON.parse(f); } catch (e) { f = []; } }
      s.filters = (Array.isArray(f) ? f : []).filter((x) => x && ['peak', 'lowShelf', 'highShelf'].includes(x.type) && x.hz > 0);
      const curve = traced(s.curve);
      if (curve) {
        const ref = curve(1000);
        s.level = (hz) => REF_DB + curve(hz) - ref;
      } else {
        const anchor = gain(s.filters, 1000);
        s.level = (hz) => REF_DB + gain(s.filters, hz) - anchor;
      }
      s.points = Array.from({ length: 241 }, (_, i) => { const hz = 20 * Math.pow(1000, i / 240); return [hz, s.level(hz)]; });
    });
    return Object.fromEntries(sounds.map((s) => [s.h, s]));
  }
  /* Measured product curve ([[hz, db], …]) -> same grid, aligned so the 1/3 octave around 1 kHz reads REF_DB */
  function measured(rec, key) {
    const pts = (rec.pts || []).filter((p) => p[0] > 0 && Number.isFinite(p[1])).sort((a, b) => a[0] - b[0]);
    if (pts.length < 2) return null;
    const lx = pts.map((p) => Math.log10(p[0]));
    const raw = (hz) => {
      const v = Math.log10(hz), n = pts.length;
      if (v <= lx[0]) return pts[0][1];
      if (v >= lx[n - 1]) return pts[n - 1][1];
      let lo = 0, hi = n - 1;
      while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (lx[mid] <= v) lo = mid; else hi = mid; }
      const t = (v - lx[lo]) / (lx[hi] - lx[lo]);
      return pts[lo][1] + t * (pts[hi][1] - pts[lo][1]);
    };
    let ref = 0;
    for (let i = 0; i < 9; i++) ref += raw(1000 * Math.pow(2, (i - 4) / 24));
    ref /= 9;
    const level = (hz) => REF_DB + raw(hz) - ref;
    return {
      h: key, name: String(rec.n || '').trim(), brand: rec.p && rec.p.b, image: rec.p && rec.p.i, dash: '', product: true, level,
      points: Array.from({ length: 241 }, (_, i) => { const hz = 20 * Math.pow(1000, i / 240); return [hz, level(hz)]; }),
    };
  }
  const hzLabel = (hz) => (hz >= 1000 ? (Math.round(hz / 100) / 10) + ' kHz' : Math.round(hz) + ' Hz');
  const dbLabel = (v) => (Math.round(v * 10) / 10).toFixed(1) + ' dB';

  /* ---------- Plot ---------- */
  function plot(svg, { curves, domain = ZOOM.all, region = null, axisX, axisY, label, mini = false }) {
    const W = 1000, H = 500, m = { l: 80, r: 30, t: 48, b: 85 };
    const width = svg.getBoundingClientRect().width || W;
    const fp = width < 360 ? 8 : width < 700 ? 9 : 12, unit = W / width;
    const lx0 = Math.log10(domain[0]), lx1 = Math.log10(domain[1]);
    const x = (hz) => m.l + (Math.log10(hz) - lx0) / (lx1 - lx0) * (W - m.l - m.r);
    const y = (db) => H - m.b - (db - DOMAIN_DB[0]) / (DOMAIN_DB[1] - DOMAIN_DB[0]) * (H - m.t - m.b);
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('lsg22-chart');
    svg.style.setProperty('--lsg22-font', (fp * unit) + 'px');
    if (label) svg.setAttribute('aria-label', label);
    svg.replaceChildren();
    svg.append(el('rect', { width: W, height: H, fill: BG }));
    const clipId = 'snd-clip-' + Math.random().toString(36).slice(2, 8);
    const defs = el('defs'), clip = el('clipPath', { id: clipId });
    clip.append(el('rect', { x: m.l, y: m.t, width: W - m.l - m.r, height: H - m.t - m.b }));
    defs.append(clip); svg.append(defs);
    if (region) {
      const rx1 = Math.max(m.l, x(Math.max(region.min, domain[0]))), rx2 = Math.min(W - m.r, x(Math.min(region.max, domain[1])));
      if (rx2 > rx1) {
        svg.append(el('rect', { class: 'ls-region-highlight', x: rx1, y: m.t, width: rx2 - rx1, height: H - m.b - m.t, fill: region.color, opacity: 0.12 }));
        [rx1, rx2].forEach((rx) => svg.append(el('line', { class: 'sw-region-boundary', x1: rx, x2: rx, y1: m.t, y2: H - m.b })));
        svg.append(el('line', { x1: rx1, x2: rx2, y1: m.t, y2: m.t, stroke: region.color, 'stroke-width': 2, opacity: 0.9 }));
      }
    }
    const g = el('g', { 'aria-hidden': 'true' });
    for (let v = 30; v <= 80; v += 10) {
      g.append(el('line', { class: 'lsg22-grid-y', x1: m.l, x2: W - m.r, y1: y(v), y2: y(v) }));
      g.append(el('text', { class: 'lsg22-axis', x: m.l - 5 * unit, y: y(v) + fp * unit / 3, 'text-anchor': 'end' }, String(v)));
    }
    MINOR.filter((f) => f > domain[0] && f < domain[1]).forEach((f) => g.append(el('line', { class: 'lsg22-grid-minor', x1: x(f), x2: x(f), y1: m.t, y2: H - m.b })));
    const shown = width < 350 ? [20, 100, 1000, 20000] : width < 550 ? [20, 100, 1000, 10000, 20000] : MAJOR;
    MAJOR.filter((f) => f >= domain[0] && f <= domain[1]).forEach((f) => {
      g.append(el('line', { class: 'lsg22-grid-major', x1: x(f), x2: x(f), y1: m.t, y2: H - m.b }));
      if (shown.includes(f) || domain !== ZOOM.all) {
        g.append(el('text', { class: 'lsg22-axis', x: x(f), y: H - m.b + (fp + 4) * unit, 'text-anchor': f === domain[0] ? 'start' : f === domain[1] ? 'end' : 'middle' }, f >= 1000 ? f / 1000 + 'k' : String(f)));
      }
    });
    g.append(el('rect', { class: 'lsg22-frame', x: m.l, y: m.t, width: W - m.l - m.r, height: H - m.t - m.b }));
    if (width >= 480) {
      const cy = (m.t + H - m.b) / 2, lx = Math.max(17, fp * unit + 2);
      if (axisY) g.append(el('text', { class: 'lsg22-axis', x: lx, y: cy, 'text-anchor': 'middle', transform: `rotate(-90 ${lx} ${cy})` }, axisY));
      if (axisX) g.append(el('text', { class: 'lsg22-axis', x: (m.l + W - m.r) / 2, y: H - 9 * unit, 'text-anchor': 'middle' }, axisX));
    }
    svg.append(g);
    const clipped = el('g', { 'clip-path': `url(#${clipId})` });
    curves.slice().reverse().forEach((c) => {
      const pts = c.sound.points.filter(([hz]) => hz >= domain[0] / 1.05 && hz <= domain[1] * 1.05);
      const d = pts.map(([hz, v], i) => (i ? 'L' : 'M') + x(hz).toFixed(1) + ' ' + y(v).toFixed(1)).join('');
      if (c.area) clipped.append(el('path', { d: d + `L${x(pts[pts.length - 1][0]).toFixed(1)} ${y(REF_DB)}L${x(pts[0][0]).toFixed(1)} ${y(REF_DB)}Z`, fill: c.sound.color, opacity: 0.06 }));
      clipped.append(el('path', { class: 'ls-curve', d, fill: 'none', stroke: c.color, 'stroke-width': 3, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-dasharray': c.sound.dash || null, opacity: c.main ? 1 : 0.85 }));
    });
    svg.append(clipped);
    const cursor = el('line', { class: 'ls-cursor', y1: m.t, y2: H - m.b, stroke: '#BDCFD8', 'stroke-width': 1, 'stroke-dasharray': '2 4', opacity: 0 });
    const dots = el('g', { class: 'ls-cursor-points', opacity: 0 });
    svg.append(cursor, dots);
    return {
      hzAt(clientX) {
        const r = svg.getBoundingClientRect();
        const vx = (clientX - r.left) / r.width * W;
        const t = Math.min(1, Math.max(0, (vx - m.l) / (W - m.l - m.r)));
        return Math.pow(10, lx0 + t * (lx1 - lx0));
      },
      cursor(hz) {
        const on = hz !== null;
        cursor.setAttribute('opacity', on ? 1 : 0);
        dots.setAttribute('opacity', on ? 1 : 0);
        if (!on) return;
        cursor.setAttribute('x1', x(hz)); cursor.setAttribute('x2', x(hz));
        dots.replaceChildren(...curves.map((c) => el('circle', { cx: x(hz), cy: y(c.sound.level(hz)), r: 3.4, fill: c.color, stroke: BG, 'stroke-width': 1.8 })));
      },
    };
  }

  /* ---------- Graph workbench (sound hero + hub) ---------- */
  function initGraph(root) {
    const cfg = json(root.querySelector('[data-snd-config]'));
    if (!cfg || !cfg.sounds.length) return;
    const t = cfg.t || {};
    const P = prepare(cfg.sounds), O = cfg.sounds.map((s) => s.h), R = cfg.regions || [];
    const get = (r) => root.querySelector('[data-role="' + r + '"]');
    const all = (sel) => [...root.querySelectorAll(sel)];
    const set = (r, v) => { const n = get(r); if (n && v !== undefined && v !== null) n.textContent = v; };
    const chart = get('chart');
    if (!chart || !P[cfg.active]) return;
    const state = { active: cfg.active, compare: [], products: [], region: 'all', zoom: 'all', hover: null };
    let api = null;

    const soundNames = () => [state.active, ...O.filter((k) => state.compare.includes(k) && k !== state.active)];
    const names = () => [...soundNames(), ...state.products];
    const lineColor = (k) => (k === state.active ? LINE[0] : LINE[1 + (names().filter((v) => v !== state.active).indexOf(k) % 5)]);
    const join = (list) => list.map((k) => P[k].name).join(t.joiner || ' / ');
    const tell = (s) => set('status', s);

    function controls() {
      const p = P[state.active];
      root.style.setProperty('--ls-profile', p.color);
      root.style.setProperty('--ls-profile-ink', p.ink);
      root.dataset.activeSignature = state.active;
      const count = get('compare-count');
      if (count) { count.textContent = state.compare.length; count.hidden = !state.compare.length; }
      const pcount = get('product-count');
      if (pcount) { pcount.textContent = state.products.length; pcount.hidden = !state.products.length; }
      syncProducts();
      const legend = get('graph-legend');
      if (legend) {
        legend.innerHTML = names().map((k) => '<span class="sw-legend-item' + (k === state.active ? ' is-main' : '') + '"><svg viewBox="0 0 20 10" aria-hidden="true"><path d="M0 5h20" stroke="'
          + lineColor(k) + '" stroke-width="3" stroke-dasharray="' + esc(P[k].dash || '') + '"/></svg>' + esc(P[k].name)
          + (k === state.active ? '' : '<button type="button" data-remove-compare="' + esc(k) + '" aria-label="' + esc(fill(t.remove, { name: P[k].name })) + '">&times;</button>') + '</span>').join('');
      }
      all('[data-profile]').forEach((i) => { i.checked = i.dataset.profile === state.active; });
      all('[data-compare]').forEach((i) => { i.checked = state.compare.includes(i.dataset.compare); i.closest('label').hidden = i.dataset.compare === state.active; });
      all('[data-zoom]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.zoom === state.zoom)));
      const sel = get('region-select');
      if (sel) sel.value = state.region;
    }
    function story() {
      const p = P[state.active];
      set('story-name', p.name); set('story-title', p.headline); set('story-intro', p.intro);
      set('story-listen', p.listen); set('story-tradeoff', p.tradeoff);
      all('[data-role=story-link], [data-role=character-shop]').forEach((a) => { a.href = p.url; });
      set('character-shop-label', fill(t.shop, { name: p.name }));
      set('story-link-label', fill(t.guide, { name: p.name }));
      const take = get('takeaway');
      if (take) {
        const others = soundNames().filter((k) => k !== state.active);
        take.hidden = !others.length;
        const key = soundNames().slice().sort().join(',');
        const pair = (cfg.pairs || []).find((x) => String(x.sounds).split(',').map((s) => s.trim()).sort().join(',') === key && x.text);
        take.textContent = pair ? pair.text : fill(t.takeaway, { names: join(soundNames()) });
      }
      root.dispatchEvent(new CustomEvent('ls:sound-change', { bubbles: true, detail: { sound: state.active, name: p.name, url: p.url } }));
    }
    function band() {
      const r = R.find((x) => x.id === state.region);
      if (!r) { set('band-name', t.full_name); set('band-range', t.full_range); set('band-body', P[state.active].reading); set('band-listen', t.full_listen); }
      else { set('band-name', r.name); set('band-range', r.range); set('band-body', r.body); set('band-listen', r.listen); }
    }
    function draw() {
      chart.hidden = false;
      const st = get('static');
      if (st) st.hidden = true;
      const r = R.find((x) => x.id === state.region);
      api = plot(chart, {
        curves: names().map((k) => ({ sound: P[k], color: lineColor(k), main: k === state.active, area: k === state.active })),
        domain: ZOOM[state.zoom] || ZOOM.all,
        region: r ? { min: r.min, max: r.max, color: P[state.active].color } : null,
        axisX: t.axis_x, axisY: t.axis_y, label: fill(t.chart_label, { names: join(names()) }),
      });
      api.cursor(state.hover);
    }
    function update() { controls(); story(); band(); draw(); }
    function choose(k) {
      if (!P[k]) return;
      state.active = k; state.compare = state.compare.filter((x) => x !== k); state.region = 'all';
      update();
      tell(fill(t.status_sound, { name: P[k].name }));
    }
    function region(id) {
      const r = R.find((x) => x.id === id);
      state.region = r ? id : 'all';
      state.zoom = 'all';
      controls(); band(); draw();
      tell(r ? fill(t.status_region, { name: r.name, range: r.range }) : t.status_all);
    }
    function probe(hz) {
      state.hover = hz;
      if (api) api.cursor(hz);
      const box = get('probe');
      if (!box) return;
      box.hidden = hz === null;
      if (hz === null) return;
      set('frequency-value', hzLabel(hz));
      const out = get('readout-values');
      if (out) out.innerHTML = names().map((k) => '<span><i style="background:' + lineColor(k) + '"></i>' + esc(P[k].name) + ' <b>' + dbLabel(P[k].level(hz)) + '</b></span>').join('');
    }

    /* Measured products from the shared feed (sections/ls-rankings-measurements.liquid), loaded on first open */
    const M = {};
    let feed = null, query = '';
    const pop = get('product-popover');
    const pcfg = cfg.products || {};
    const pmax = Math.max(1, Number(pcfg.max) || 3);
    function syncProducts() {
      const list = get('product-list');
      if (!list) return;
      const full = state.products.length >= pmax;
      list.querySelectorAll('[data-product-add]').forEach((b) => {
        const k = b.dataset.productAdd, on = state.products.includes(k);
        b.setAttribute('aria-pressed', String(on));
        b.disabled = full && !on;
        b.style.setProperty('--color', on ? lineColor(k) : 'transparent');
      });
      const note = get('product-note');
      if (note && feed && !list.querySelector('.sw-product-skel')) {
        const shown = list.querySelectorAll('li:not([hidden])').length;
        note.textContent = !shown ? (t.products_empty || '') : full ? (t.products_full || '') : '';
        note.hidden = !note.textContent;
      }
    }
    function filterProducts() {
      const list = get('product-list');
      if (!list) return;
      const q = query.trim().toLowerCase();
      list.querySelectorAll('li[data-search]').forEach((li) => { li.hidden = !!q && !li.dataset.search.includes(q); });
      syncProducts();
    }
    function renderProducts() {
      const list = get('product-list');
      if (!list) return;
      list.innerHTML = Object.values(M).sort((a, b) => a.name.localeCompare(b.name)).map((p) => '<li data-search="'
        + esc((p.name + ' ' + (p.brand || '')).toLowerCase()) + '"><button type="button" class="sw-product-option" data-product-add="' + esc(p.h) + '" aria-pressed="false">'
        + (p.image ? '<img src="' + esc(p.image) + '" alt="" width="40" height="40" loading="lazy">' : '<span class="sw-product-thumb"></span>')
        + '<span class="sw-product-name">' + esc(p.name) + (p.brand ? '<small>' + esc(p.brand) + '</small>' : '') + '</span><i aria-hidden="true"></i></button></li>').join('');
      filterProducts();
    }
    async function loadProducts() {
      if (feed) return feed;
      feed = fetch(pcfg.url, { credentials: 'same-origin' }).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); }).then((html) => {
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ls-measurements]');
        (json(node) || []).forEach((rec) => {
          const key = 'm:' + (rec.h || rec.id);
          if (M[key]) return;
          const p = measured(rec, key);
          if (p) M[key] = p;
        });
        get('product-note')?.classList.remove('ls-loading');
        renderProducts();
      }).catch(() => {
        feed = null;
        const note = get('product-note');
        get('product-list')?.querySelectorAll('.sw-product-skel').forEach((li) => li.remove());
        if (note) { note.classList.remove('ls-loading'); note.hidden = false; note.textContent = t.products_error || ''; }
      });
      return feed;
    }
    function toggleProduct(k) {
      const p = M[k];
      if (!p) return;
      if (state.products.includes(k)) state.products = state.products.filter((x) => x !== k);
      else {
        if (state.products.length >= pmax) return;
        P[k] = p;
        state.products = [...state.products, k];
        tell(fill(t.status_product, { name: p.name }));
      }
      controls(); story(); draw();
    }
    if (pop) {
      pop.addEventListener('toggle', () => {
        if (!pop.open) return;
        loadProducts();
        if (window.matchMedia('(pointer: fine)').matches) requestAnimationFrame(() => get('product-search')?.focus());
      });
      get('product-search')?.addEventListener('input', (e) => { query = e.target.value; filterProducts(); });
    }

    root.addEventListener('change', (e) => {
      const tgt = e.target;
      if (tgt.matches('[data-profile]')) choose(tgt.dataset.profile);
      else if (tgt.matches('[data-compare]')) {
        const k = tgt.dataset.compare;
        state.compare = tgt.checked ? [...new Set([...state.compare, k])] : state.compare.filter((x) => x !== k);
        controls(); story(); draw();
      } else if (tgt.matches('[data-role=region-select]')) region(tgt.value);
    });
    root.addEventListener('click', (e) => {
      const b = e.target.closest('button, [data-compare-contrast]');
      if (!b || !root.contains(b)) return;
      if (b.dataset.removeCompare) {
        const k = b.dataset.removeCompare;
        state.compare = state.compare.filter((x) => x !== k); state.products = state.products.filter((x) => x !== k);
        controls(); story(); draw();
      } else if (b.dataset.productAdd) toggleProduct(b.dataset.productAdd);
      else if (b.dataset.action === 'all') { state.compare = O.filter((k) => k !== state.active); controls(); story(); draw(); }
      else if (b.dataset.action === 'solo') { state.compare = []; controls(); story(); draw(); }
      else if (b.dataset.preset) {
        const [a, ...rest] = b.dataset.preset.split(',').map((s) => s.trim()).filter((k) => P[k]);
        if (a) { state.active = a; state.compare = rest; state.region = 'all'; update(); }
        b.closest('details')?.removeAttribute('open');
      } else if (b.dataset.close) b.closest('details')?.removeAttribute('open');
      else if (b.dataset.zoom) { state.zoom = ZOOM[b.dataset.zoom] ? b.dataset.zoom : 'all'; controls(); draw(); }
      else if (b.dataset.compareContrast) {
        const k = b.dataset.compareContrast;
        if (!P[k]) return;
        state.compare = [k]; controls(); story(); draw();
        const stage = root.querySelector('[data-snd-stage]');
        stage?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      }
    });
    chart.addEventListener('pointermove', (e) => { if (api) probe(api.hzAt(e.clientX)); });
    chart.addEventListener('pointerleave', () => probe(null));
    chart.addEventListener('click', (e) => {
      if (!api || !R.length || !get('band-response')) return;
      const hz = api.hzAt(e.clientX);
      const r = R.find((x) => hz >= x.min && hz < x.max);
      if (r) region(r.id === state.region ? 'all' : r.id);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      all('details.sw-popover[open]').forEach((d) => { d.removeAttribute('open'); d.querySelector('summary')?.focus(); });
    });
    document.addEventListener('click', (e) => {
      all('details.sw-popover[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
    let rz = 0;
    new ResizeObserver(() => { cancelAnimationFrame(rz); rz = requestAnimationFrame(draw); }).observe(chart.parentElement);
    update();
  }

  /* ---------- Hub: use contexts follow the selected sound ---------- */
  function initUses(sec) {
    const label = sec.dataset.withLabel || '{name}';
    const apply = (detail) => {
      sec.querySelectorAll('[data-use-context]').forEach((box) => {
        const rows = json(box.querySelector('script')) || {};
        const row = rows[detail.sound];
        const title = box.querySelector('[data-use-title]'), text = box.querySelector('[data-use-text]');
        box.hidden = !row;
        if (!row) return;
        if (title) title.textContent = fill(label, { name: detail.name, title: row[0] });
        if (text) text.textContent = row[1];
      });
      sec.querySelectorAll('[data-use-shop]').forEach((a) => {
        a.href = detail.url + '?use=' + encodeURIComponent(a.dataset.useShop) + '#products';
        const span = a.querySelector('[data-use-shop-label]');
        if (span) span.textContent = fill(a.dataset.label, { name: detail.name });
      });
      sec.querySelectorAll('[data-role=uses-signature]').forEach((n) => { n.textContent = detail.name; });
    };
    document.addEventListener('ls:sound-change', (e) => apply(e.detail));
  }
  function initNavShop(nav) {
    document.addEventListener('ls:sound-change', (e) => {
      const a = nav.querySelector('[data-nav-shop]');
      if (a) a.href = e.detail.url;
      nav.querySelectorAll('[data-role=nav-signature]').forEach((n) => { n.textContent = e.detail.name; });
      const label = nav.querySelector('[data-role=nav-shop-label]');
      if (label) label.textContent = fill(label.dataset.template, { name: e.detail.name });
    });
    const row = nav.querySelector('.ls-chapter-links');
    if (!row) return;
    const edges = () => {
      const max = row.scrollWidth - row.clientWidth;
      row.dataset.overflow = max <= 1 ? '' : row.scrollLeft <= 1 ? 'end' : row.scrollLeft >= max - 1 ? 'start' : 'both';
    };
    row.addEventListener('scroll', edges, { passive: true });
    new ResizeObserver(edges).observe(row);
    edges();
  }

  /* ---------- Genre artwork (prototype listening-notes covers) ---------- */
  function artwork(kind, a, b, name, caption) {
    let body = '';
    if (kind === 'keys') {
      for (let i = 0; i < 12; i++) body += `<rect x="${20 + i * 41}" y="110" width="38" height="310" rx="5" fill="${a}" opacity="${0.27 + (i % 4) * 0.18}"/>`;
      for (const i of [0, 1, 3, 4, 5, 7, 8, 10]) body += `<rect x="${46 + i * 41}" y="110" width="24" height="190" rx="3" fill="${b}"/>`;
      body = '<g transform="rotate(-15 270 270)">' + body + '</g>';
    } else if (kind === 'disc') {
      for (let i = 0; i < 21; i++) body += `<circle cx="280" cy="250" r="${215 - i * 8}" fill="none" stroke="${a}" stroke-width="${i % 4 === 0 ? 3 : 0.8}" opacity="${i % 4 === 0 ? 0.7 : 0.3}"/>`;
      body += `<circle cx="280" cy="250" r="51" fill="${a}"/><circle cx="280" cy="250" r="8" fill="${b}"/><path d="M461 81L420 274 354 322" fill="none" stroke="${a}" stroke-width="9" stroke-linecap="round"/>`;
    } else if (kind === 'strings') {
      body += `<ellipse cx="230" cy="286" rx="172" ry="141" fill="${a}" opacity=".16" transform="rotate(-26 230 286)"/><ellipse cx="230" cy="286" rx="120" ry="98" fill="none" stroke="${a}" stroke-width="2" opacity=".65"/>`;
      for (let i = 0; i < 12; i++) body += `<path d="M${50 + i * 21} 472L${155 + i * 27} 38" stroke="${a}" stroke-width="${1 + i / 6}" opacity=".75"/>`;
      for (let i = 0; i < 11; i++) body += `<path d="M${130 + i * 8} ${90 + i * 28}l188 70" stroke="${a}" stroke-width=".8" opacity=".3"/>`;
    } else if (kind === 'pulse') {
      for (let i = 0; i < 17; i++) body += `<rect x="${50 + i * 25}" y="${100 + Math.sin(i * 0.6) * 58}" width="12" height="${195 + Math.cos(i * 0.45) * 120}" fill="${a}" opacity="${0.27 + (i % 5) * 0.12}" rx="6" transform="rotate(-16 270 270)"/>`;
    } else if (kind === 'rays') {
      for (let i = 0; i < 30; i++) { const r = i * 2 * Math.PI / 30; body += `<line x1="${270 + 65 * Math.cos(r)}" y1="${258 + 65 * Math.sin(r)}" x2="${270 + 225 * Math.cos(r)}" y2="${258 + 225 * Math.sin(r)}" stroke="${a}" stroke-width="${i % 3 === 0 ? 8 : 2}" opacity=".6"/>`; }
      body += `<circle cx="270" cy="258" r="42" fill="${a}"/>`;
    } else if (kind === 'arcs') {
      for (let i = 0; i < 17; i++) body += `<path d="M${50 + i * 14} 430V${245 - i * 10}a${220 - i * 12} ${190 - i * 9} 0 0 1 ${440 - i * 24} 0V430" stroke="${a}" stroke-width="${i % 3 === 0 ? 3 : 1}" opacity=".52" fill="none"/>`;
    } else {
      for (let i = 0; i < 28; i++) body += `<path d="M20 ${140 + i * 8}C130 ${20 + i * 9} 200 ${350 - i * 2} 285 ${200 + i * 4}S410 ${80 + i * 10} 520 ${130 + i * 10}" stroke="${a}" stroke-width="1.4" opacity=".5" fill="none"/>`;
    }
    const top = caption ? `<text x="44" y="65" fill="${a}" font-family="Arial,sans-serif" font-size="12">${esc(caption)}</text>` : '';
    return `<svg viewBox="0 0 540 540" aria-hidden="true" focusable="false"><rect width="540" height="540" fill="${b}"/><rect x="14" y="14" width="512" height="482" rx="5" fill="${a}" opacity=".05"/><rect x="26" y="26" width="488" height="455" rx="2" fill="none" stroke="${a}" opacity=".24"/>${body}${top}<text x="44" y="462" fill="${a}" font-family="Arial,sans-serif" font-size="16">${esc(name)}</text></svg>`;
  }
  function paintArt(scope) {
    scope.querySelectorAll('[data-genre-art]').forEach((n) => {
      const d = n.dataset;
      n.innerHTML = artwork(d.genreArt, d.accent || '#fff', d.background || '#16171a', d.name || '', d.caption || '');
    });
  }

  /* ---------- Mini comparison graphs (genre pages, genre carousel) ---------- */
  let soundData = null;
  function soundsFrom(node) {
    if (!soundData) {
      const src = document.querySelector('[data-snd-sounds]');
      soundData = src ? prepare(json(src) || []) : {};
    }
    return soundData;
  }
  function miniGraph(svg, keys, t) {
    const P = soundsFrom(svg);
    const curves = keys.filter((k) => P[k]).map((k, i) => ({ sound: P[k], color: LINE[i % LINE.length], main: i === 0 }));
    if (!curves.length) return;
    const draw = () => plot(svg, { curves, axisX: t.axis_x, axisY: t.axis_y, label: fill(t.chart_label, { names: curves.map((c) => c.sound.name).join(t.joiner || ' / ') }), mini: true });
    draw();
    if (!svg._sndObserved) {
      svg._sndObserved = true;
      let rz = 0;
      new ResizeObserver(() => { cancelAnimationFrame(rz); rz = requestAnimationFrame(() => (svg._sndDraw || draw)()); }).observe(svg.parentElement);
    }
    svg._sndDraw = draw;
  }
  function legendHTML(keys) {
    const P = soundsFrom();
    return keys.filter((k) => P[k]).map((k, i) => `<span><svg viewBox="0 0 28 10" aria-hidden="true"><path d="M0 5h28" stroke="${LINE[i % LINE.length]}" stroke-width="3" stroke-dasharray="${esc(P[k].dash || '')}"/></svg>${esc(P[k].name)}</span>`).join('');
  }
  function initMini(fig) {
    const t = json(fig.querySelector('[data-snd-mini-t]')) || {};
    const keys = String(fig.dataset.sounds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const svg = fig.querySelector('svg[data-role=mini-chart]');
    const legend = fig.querySelector('[data-role=mini-legend]');
    if (legend) legend.innerHTML = legendHTML(keys);
    if (svg) { svg.hidden = false; miniGraph(svg, keys, t); }
  }

  /* ---------- Hub genre carousel ---------- */
  function initGenres(sec) {
    const slides = [...sec.querySelectorAll('[data-genre-slide]')];
    const tabs = [...sec.querySelectorAll('[data-genre-tab]')];
    const select = sec.querySelector('[data-role=genre-select]');
    const counter = sec.querySelector('[data-role=genre-counter]');
    const t = json(sec.querySelector('[data-snd-mini-t]')) || {};
    if (!slides.length) return;
    let index = Math.max(0, slides.findIndex((s) => !s.hidden));
    function show(i, focus = false, dir = 1, animate = true) {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => { s.hidden = n !== index; });
      tabs.forEach((b, n) => { b.setAttribute('aria-selected', String(n === index)); b.tabIndex = n === index ? 0 : -1; });
      if (select) select.value = String(index);
      if (counter) counter.textContent = fill(counter.dataset.template, { current: index + 1, total: slides.length });
      const slide = slides[index];
      const svg = slide.querySelector('svg[data-role=mini-chart]');
      const keys = String(slide.dataset.sounds || '').split(',').filter(Boolean);
      if (svg) { svg.hidden = false; miniGraph(svg, keys, t); }
      const legend = slide.querySelector('[data-role=mini-legend]');
      if (legend) legend.innerHTML = legendHTML(keys);
      if (animate && window.gsap && !reduced()) window.gsap.fromTo(slide, { autoAlpha: 0, x: 18 * dir }, { autoAlpha: 1, x: 0, duration: 0.32, ease: 'power2.out', clearProps: 'transform,opacity,visibility' });
      if (focus) tabs[index]?.focus();
    }
    sec.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-genre-tab]');
      if (tab) return show(Number(tab.dataset.genreTab), false, Number(tab.dataset.genreTab) >= index ? 1 : -1);
      if (e.target.closest('[data-action=genre-prev]')) show(index - 1, false, -1);
      if (e.target.closest('[data-action=genre-next]')) show(index + 1, false, 1);
    });
    sec.addEventListener('keydown', (e) => {
      if (!e.target.closest('[data-genre-tab]')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1, true, 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1, true, -1); }
      if (e.key === 'Home') { e.preventDefault(); show(0, true, -1); }
      if (e.key === 'End') { e.preventDefault(); show(slides.length - 1, true, 1); }
    });
    select?.addEventListener('change', () => show(Number(select.value)));
    sec.classList.add('is-live');
    show(index, false, 1, false);
  }

  function start() {
    document.querySelectorAll('[data-snd-uses]').forEach(initUses);
    document.querySelectorAll('[data-snd-subnav]').forEach(initNavShop);
    document.querySelectorAll('[data-snd-graph]').forEach(initGraph);
    paintArt(document);
    document.querySelectorAll('[data-snd-mini]').forEach(initMini);
    document.querySelectorAll('[data-snd-genres]').forEach(initGenres);
    document.querySelectorAll('.ls-snd').forEach((n) => n.classList.add('snd-live'));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
